import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ListViewSectionFilterLink} from '@github-ui/list-view/ListViewSectionFilterLink'
import {ListLoading} from '@github-ui/list-view-items-issues-prs/ListLoading'
import {replaceDateTokens} from '@github-ui/list-view-items-issues-prs/Query'
import {useLocation} from '@github-ui/react-router'
import PreloadedQueryBoundary from '@github-ui/relay-preloaded-query-boundary'
import {startTransition, Suspense, useEffect, useMemo, useRef} from 'react'
import {graphql, type PreloadedQuery, usePreloadedQuery} from 'react-relay'

import {VALUES} from '../../constants/values'
import {useEntryPointsLoader} from '../../hooks/use-entrypoint-loaders'
import {addPagingParams} from '../../utils/urls'
import {ListError} from '../list/ListError'
import {SearchList} from '../search/SearchList'
import type {
  SavedViewIssueListQuery,
  SavedViewIssueListQuery$variables,
} from './__generated__/SavedViewIssueListQuery.graphql'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssueListQuery = graphql`
  query SavedViewIssueListQuery(
    $owner: String!
    $name: String!
    $query: String!
    $first: Int = 25
    $labelPageSize: Int = 20
    $skip: Int = null
    $type: SearchType = ISSUE_ADVANCED
    $showIssueFieldPills: Boolean = false
  ) {
    repository(owner: $owner, name: $name) {
      id
      ...SearchListRepo
      ...SearchList
        @arguments(
          query: $query
          first: $first
          labelPageSize: $labelPageSize
          skip: $skip
          fetchRepository: false
          type: $type
          showIssueFieldPills: $showIssueFieldPills
        )
    }
  }
`

type SavedViewIssueListProps = {
  owner: string
  repo: string
  query: string
  preheatEnabled?: boolean
}

export function SavedViewIssueList({owner, repo, query, preheatEnabled}: SavedViewIssueListProps) {
  const {queryRef, loadQuery} = useEntryPointsLoader<SavedViewIssueListQuery>(null, IssueListQuery)
  const {search: urlSearch} = useLocation()

  const variables = useMemo<SavedViewIssueListQuery$variables>(() => {
    const vars: SavedViewIssueListQuery$variables = {
      owner,
      name: repo,
      query: replaceDateTokens(query),
      showIssueFieldPills: isFeatureEnabled('issue_fields_index_pills'),
    }
    addPagingParams(new URLSearchParams(urlSearch), vars)
    return vars
  }, [owner, repo, query, urlSearch])

  useEffect(() => {
    startTransition(() => {
      loadQuery(variables)
    })
  }, [loadQuery, variables])

  const loadingFallback = (
    <ListLoading
      showBorder
      layoutDensity="default"
      pageSize={VALUES.issuesPageSize('')}
      sectionFilters={[
        <ListViewSectionFilterLink key="open" title="Open" isLoading href="" />,
        <ListViewSectionFilterLink key="closed" title="Closed" isLoading href="" />,
      ]}
    />
  )

  return (
    <Suspense fallback={loadingFallback}>
      <PreloadedQueryBoundary
        critical
        fallback={ListError}
        onRetry={() => {
          loadQuery(variables, {fetchPolicy: 'network-only'})
        }}
      >
        {queryRef ? (
          <IssueListContent queryRef={queryRef} query={query} preheatEnabled={preheatEnabled} />
        ) : (
          loadingFallback
        )}
      </PreloadedQueryBoundary>
    </Suspense>
  )
}

type IssueListContentProps = {
  queryRef: PreloadedQuery<SavedViewIssueListQuery>
  query: string
  preheatEnabled?: boolean
}

function IssueListContent({queryRef, query, preheatEnabled}: IssueListContentProps) {
  const data = usePreloadedQuery<SavedViewIssueListQuery>(IssueListQuery, queryRef)
  const listRef = useRef<HTMLUListElement | undefined>(undefined)

  if (!data.repository) {
    throw new Error('Repository not found')
  }

  return (
    <SearchList
      itemIdentifier={undefined}
      query={query}
      search={data.repository}
      repository={data.repository}
      listRef={listRef}
      preheatEnabled={preheatEnabled}
    />
  )
}
